import { useState } from 'react'
import { Link } from 'react-router'
import { IconButton } from '../../design/components'
import { useSetCharacterHidden } from './queries'
import styles from './CharacterMenu.module.css'

export interface CharacterMenuProps {
  characterId: number
  /** The character's name, so the menu's controls say what they act on. */
  name: string
  /** Whether the signed-in user has already hidden this character. */
  isHidden: boolean
}

/**
 * The sheet's overflow menu: the actions that belong to the character as a whole rather than
 * to one of its cards.
 *
 * It exists because there is now more than one such action. The printable sheet used to sit
 * alone in the header slot; hiding is the second, and a header that grows an icon per action
 * is how a 390px screen loses its title. Same shape as the account menu — trigger, scrim,
 * panel — so the two behave identically wherever they appear.
 */
export function CharacterMenu({ characterId, name, isHidden }: CharacterMenuProps) {
  const [open, setOpen] = useState(false)
  const setHidden = useSetCharacterHidden(characterId)

  return (
    <div className={styles.wrap}>
      <IconButton
        label={`Actions for ${name}`}
        icon="more"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      />

      {open && (
        <>
          <div className={styles.scrim} onClick={() => setOpen(false)} aria-hidden="true" />
          {/* A dialog, not an ARIA menu — same choice as the account menu. `role="menu"`
              promises arrow-key roving focus, and a panel of two native controls that does not
              deliver it is worse for a screen reader than one that never claimed to. */}
          <div className={styles.panel} role="dialog" aria-label={`Actions for ${name}`}>
            <Link
              to={`/characters/${characterId}/print`}
              className={styles.item}
              onClick={() => setOpen(false)}
            >
              Printable sheet
            </Link>

            {/* The menu stays open while the request is in flight and closes on the answer:
                the label is the only feedback this action has — nothing else on the sheet
                changes — so closing first would leave the reader unsure it took. */}
            <button
              type="button"
              className={styles.item}
              disabled={setHidden.isPending}
              onClick={() => {
                setHidden.mutate(!isHidden, { onSuccess: () => setOpen(false) })
              }}
            >
              {setHidden.isPending
                ? 'Saving…'
                : isHidden
                  ? 'Show in my lists'
                  : 'Hide from my lists'}
            </button>

            <p className={styles.note}>
              {isHidden
                ? 'Hidden characters stay out of your roster, searches and pickers. Only you see this.'
                : 'Hiding keeps a character out of your roster, searches and pickers. It stays in its campaigns and scenes, and nobody else is affected.'}
            </p>

            {setHidden.isError && <p className={styles.error}>That did not save. Try again.</p>}
          </div>
        </>
      )}
    </div>
  )
}
